import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { GlobalErrorService } from '@services/global-error.service';
import { environment } from '@environments/environment';
import memoizeObservable from '@app/shared/utils/memoizedObservable';
import {
  RtoDetails,
  RtoEmployeeMonth,
  TeamManagementCardsResponse,
  TeamCompositionDetailsResponse,
  TeamCompositionListResponse,
  Project,
  ChangeTeamResponse,
  reporteeListResponse,
  Reportee,
  RtoCardResponse,
  newBeginningsRequest,
  feedbackCountResponse,
} from '@interfaces/team-management.model';
import { ToastrService } from 'ngx-toastr';
import {
  RtoDetailsEnum,
  TeamCards,
  ErrorMessages,
  RtoCard,
} from '@constants/global.enum';
import { dropdownValues } from '@interfaces/common.model';

@Injectable({
  providedIn: 'root',
})
export class TeamManagementService {
  private baseUrl = environment.apiUrl;
  private selectedDropdownValue$ = new BehaviorSubject<{
    employeeId: string;
    filterType?: string;
  } | null>(null);
  private reporteeData$ = new BehaviorSubject<dropdownValues[]>([]);

  constructor(
    private http: HttpClient,
    private errorHandler: GlobalErrorService,
    private toasterService: ToastrService,
  ) {}

  public getSelectedDropdownValue(): Observable<{
    employeeId: string;
    filterType?: string;
  } | null> {
    return this.selectedDropdownValue$.asObservable();
  }

  public setSelectedDropdownValue(
    value: { employeeId: string; filterType?: string } | null,
  ): void {
    this.selectedDropdownValue$.next(value);
  }

  public getCurrentSelectedDropdownValue(): {
    employeeId: string;
    filterType?: string;
  } | null {
    return this.selectedDropdownValue$.value;
  }

  public getReporteeData(): Observable<dropdownValues[]> {
    return this.reporteeData$.asObservable();
  }

  public setReporteeData(data: dropdownValues[]): void {
    this.reporteeData$.next(data);
  }

  getTeamCardsData(
    employeeId: string,
    filterType?: string,
  ): Observable<TeamManagementCardsResponse[]> {
    let url = `${this.baseUrl}/team-management/overview?employeeId=${employeeId}`;
    if (filterType) {
      url += `&filterType=${filterType}`;
    }
    return this.http.get<TeamManagementCardsResponse[]>(url).pipe(
      catchError((error) => {
        this.toasterService.error(TeamCards.errorMessage);
        return throwError(() => error);
      }),
    );
  }

  getRtoDetails(
    employeeId: string,
    selection: string,
    startDate?: string,
    endDate?: string,
    filterType?: string,
    months?: string,
  ): Observable<{ data: RtoDetails[] }> {
    let url = `${this.baseUrl}/team-management/team-rto-details/${employeeId}?selection=${encodeURIComponent(selection)}`;
    if (startDate && endDate) {
      url += `&startDate=${startDate}&endDate=${endDate}`;
    }
    if (filterType) {
      url += `&filterType=${filterType}`;
    }
    if (months) {
      url += `&months=${encodeURIComponent(months)}`;
    }

    return this.http.get<{ data: RtoDetails[] }>(url).pipe(
      catchError((error) => {
        this.toasterService.error(RtoDetailsEnum.errorMessage);
        return throwError(() => error);
      }),
    );
  }

  /** Day-level RTO drill-down for one employee: attendance grouped by month
   *  with the exact dates, for the slide-over panel on the RTO page. */
  getEmployeeRtoDays(
    employeeId: string,
    selection: string,
    startDate?: string,
    endDate?: string,
    months?: string,
  ): Observable<{ data: RtoEmployeeMonth[] }> {
    let url = `${this.baseUrl}/team-management/team-rto-employee-days/${employeeId}?selection=${encodeURIComponent(selection)}`;
    if (startDate && endDate) {
      url += `&startDate=${startDate}&endDate=${endDate}`;
    }
    if (months) {
      url += `&months=${encodeURIComponent(months)}`;
    }
    return this.http.get<{ data: RtoEmployeeMonth[] }>(url).pipe(
      catchError((error) => {
        this.toasterService.error(RtoDetailsEnum.errorMessage);
        return throwError(() => error);
      }),
    );
  }

  getRtoData(
    employeeId: string,
    filterType?: string,
  ): Observable<{ data: RtoCardResponse[] }> {
    let url = `${this.baseUrl}/team-management/rto-list?employeeId=${employeeId}&includeCurrentMonth=false`;
    if (filterType) {
      url += `&filterType=${filterType}`;
    }
    return this.http.get<{ data: RtoCardResponse[] }>(url).pipe(
      catchError((error) => {
        this.toasterService.error(RtoCard.noDataAvailable);
        return throwError(() => error);
      }),
    );
  }

  getTeamCompositionDetails(
    employeeId: string,
  ): Observable<TeamCompositionDetailsResponse> {
    return this.http
      .get<TeamCompositionDetailsResponse>(
        `${this.baseUrl}/team-management/team-composition-info?employeeId=${employeeId}`,
      )
      .pipe(
        catchError((error) => {
          return this.errorHandler.logError(error);
        }),
      );
  }

  getTeamCompositionList(
    employeeId: string,
    filterType?: string,
  ): Observable<TeamCompositionListResponse> {
    let url = `${this.baseUrl}/team-management/team-composition-list/${employeeId}`;
    if (filterType) {
      url += `?filterType=${filterType}`;
    }
    return this.http.get<TeamCompositionListResponse>(url).pipe(
      catchError((error) => {
        return this.errorHandler.logError(error);
      }),
    );
  }

  /** Team Composition action: set/clear the employee's exclude_from_iskill flag. */
  setIskillExclusion(
    employeeId: string,
    exclude: boolean,
  ): Observable<{ status: string; message?: string }> {
    const url = `${this.baseUrl}/team-management/team-composition/${encodeURIComponent(employeeId)}/iskill-exclusion`;
    return this.http
      .put<{ status: string; message?: string }>(url, { exclude })
      .pipe(catchError((error) => throwError(() => error)));
  }

  /** Team Composition action: mark the employee InActive with a mandatory last working day (yyyy-MM-dd). */
  deactivateEmployee(
    employeeId: string,
    lastWorkingDay: string,
  ): Observable<{ status: string; message?: string }> {
    const url = `${this.baseUrl}/team-management/team-composition/${encodeURIComponent(employeeId)}/deactivate`;
    return this.http
      .put<{ status: string; message?: string }>(url, { lastWorkingDay })
      .pipe(catchError((error) => throwError(() => error)));
  }

  addException(exceptionData: any): Observable<any> {
    const url = `${this.baseUrl}/team-management/add-exception`;
    return this.http.post<any>(url, exceptionData).pipe(
      catchError((error) => {
        this.toasterService.error(ErrorMessages.SubmissionFailed);
        return throwError(() => error);
      }),
    );
  }

  getProjectData(employeeId: string): Observable<Project[]> {
    const url = `${this.baseUrl}/team-management/employee-project-details/${employeeId}`;
    return this.http.get<Project[]>(url).pipe(
      catchError((error) => {
        this.toasterService.error(ErrorMessages.NoDataAvailable);
        return throwError(() => error);
      }),
    );
  }

  submitChangeTeam(changeTeamData: ChangeTeamResponse): Observable<any> {
    const url = `${this.baseUrl}/team-management/employee-project-details`;
    return this.http.post<any>(url, changeTeamData).pipe(
      catchError((error) => {
        this.toasterService.error(ErrorMessages.SubmissionFailed);
        return throwError(() => error);
      }),
    );
  }

  getTimesheetStatusCount(employeeId: string, filterType?: string): Observable<{ status: string; reason: string; data: { submittedCount: number; approvedCount: number } }> {
    let url = `${this.baseUrl}/vendor/timesheet-status-count?employeeId=${employeeId}`;
    if (filterType) {
      url += `&filterType=${filterType}`;
    }
    return this.http
      .get<{ status: string; reason: string; data: { submittedCount: number; approvedCount: number } }>(url)
      .pipe(
        catchError((error) => {
          return throwError(() => error);
        }),
      );
  }

  getReporteesList = memoizeObservable((employeeId: string) => {
    return this.http.get<reporteeListResponse>(
      `${this.baseUrl}/team-management/direct-reportee-details?employeeIds=${employeeId}`,
    );
  });

  /**
   * Transforms reportees API response into dropdown options array
   * @param response - The API response containing reportees data
   * @returns Array of dropdown options with All Teams and Direct Reportees options if available
   */
  transformReporteesToDropdownOptions(
    response: reporteeListResponse,
  ): dropdownValues[] {
    const reporteeData: dropdownValues[] = [];

    // Map employee data to dropdown format
    const employeeOptions = (response?.data?.data || []).map(
      (item: Reportee) => ({
        label: item.fullName,
        value: item.employeeId,
      }),
    );

    // Add 'All Teams' option always
    reporteeData.push({
      label: 'All Teams',
      value: 'all',
      selected: true,
    });

    // Add 'Direct Reportees' option always
    reporteeData.push({
      label: 'Direct Reportees',
      value: 'dr',
    });

    // Combine static options with employee options
    return [...reporteeData, ...employeeOptions];
  }

  createNewBeginning(
    newBeginningData: newBeginningsRequest,
  ): Observable<newBeginningsRequest> {
    const url = `${this.baseUrl}/team-management/create-new-beginning`;
    return this.http.post<newBeginningsRequest>(url, newBeginningData).pipe(
      catchError((error) => {
        return this.errorHandler.logError(error);
      }),
    );
  }

  getQuarterlyFeedbackCount(
    empId: string,
    quarter: string,
    filterType?: string,
  ): Observable<feedbackCountResponse> {
    let url = `${this.baseUrl}/hiring/feedback-count-summary?hiringManagerId=${empId}&quarter=${quarter}`;
    if (filterType) {
      url += `&isDirectReportees=true`;
    }
    return this.http.get<feedbackCountResponse>(url).pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  /**
   * Feedback list for a manager's subtree in a chosen quarter — powers the
   * drill-down modal from the Performance Rating tile. Reuses the existing
   * /ops/quarterly-feedback endpoint which already returns Name / Rating /
   * Comments / L3 leader per employee.
   */
  getQuarterlyFeedbackList(
    employeeId: string,
    year: number,
    quarter: string,
  ): Observable<{
    status: string;
    data: Array<{
      employeeId: string;
      employeeName: string;
      reportingManagerName?: string;
      l3Name?: string;
      l4Name?: string;
      l5Name?: string;
      performanceRating?: string | null;
      comments?: string[];
      submittedDate?: string;
    }> | null;
  }> {
    const params = new URLSearchParams({
      quarter,
      year: String(year),
      employeeId,
    });
    return this.http
      .get<{
        status: string;
        data: Array<{
          employeeId: string;
          employeeName: string;
          reportingManagerName?: string;
          l3Name?: string;
          l4Name?: string;
          l5Name?: string;
          performanceRating?: string | null;
          comments?: string[];
        }> | null;
      }>(`${this.baseUrl}/ops/quarterly-feedback?${params.toString()}`)
      .pipe(catchError((error) => throwError(() => error)));
  }

  /**
   * Per-rating tally for a manager's CWRs in a chosen quarter — powers the
   * Performance Rating tile on the Team Dashboard. filterType defaults to
   * "AllReportees" (recursive subtree); "DirectReportees" limits to direct
   * reports only, matching the convention on other Team Dashboard endpoints.
   */
  getPerformanceRatingSummary(
    employeeId: string,
    year: number,
    quarter: string,
    filterType?: string,
  ): Observable<{
    status: string;
    data: {
      year: number;
      quarter: string;
      counts: Record<string, number>;
      totalCwr: number;
      ratedCount: number;
      unratedCount: number;
    } | null;
  }> {
    const params = new URLSearchParams({
      employeeId,
      year: String(year),
      quarter,
      filterType: filterType || 'AllReportees',
    });
    return this.http
      .get<{
        status: string;
        data: {
          year: number;
          quarter: string;
          counts: Record<string, number>;
          totalCwr: number;
          ratedCount: number;
          unratedCount: number;
        } | null;
      }>(`${this.baseUrl}/hiring/contractor-feedback/performance-rating-summary?${params.toString()}`)
      .pipe(catchError((error) => throwError(() => error)));
  }
}
